import React, { useState } from 'react'
import { injectIntl } from 'react-intl'
import { Radio } from '@material-ui/core'
import { StyledName } from 'components/common/Modals/styles'
import messages from 'routes/App/Apps/JobsTriggers/translate'
import SelectInput from 'components/common/Inputs/SelectInput/SelectInput'
import {
  DAY_OF_MONTH,
  DAY_OF_WEEK,
  DAYS_OF_MONTH,
  DAYS_OF_WEEK,
  DAYS_OF_WEEK_OPTIONS,
  DAYS_OF_WEEK_SHORTENED_OPTIONS,
  END_DATE_AFTER_NUMBER,
  END_DATE_AFTER_TERM,
  END_DATE_TERMS,
  FREQUENCY,
  REGULAR_REPEAT_SCHEDULE,
  REPEAT,
  REPEAT_BY,
  VALUE_MONTHS,
  VALUE_REGULAR,
  VALUE_WEEKS,
  WEEK_NUMBERS,
  WEEK_ORDINARY_NUMBER,
  FORMATTED_REPEAT_ACTIONS,
  FORMATTED_STOP_RECURRENCE_ACTIONS,
} from 'constants/jobsTriggers'
import { TriggersScheduleProps } from './TriggersSchedule.types'
import {
  DaysContainer,
  Day,
  StyledCondition,
  EveryTextField,
  RadioContainer,
  OccurrencesTextField,
  OccurrencesContainer,
  Wrapper,
} from './TriggersSchedule.styles'

function TriggersSchedule({
  intl: { formatMessage },
  isFetching,
  onChangeRecurrence,
  formData,
  isStartCondition,
  validationErrors,
  onChangeRegularRepeat,
  onClearMonthData,
}: TriggersScheduleProps) {
  const isRegular = formData[REPEAT] === VALUE_REGULAR
  const [monthRepeatType, setMonthRepeatType] = useState(0)

  const formatOptions = (
    options: Array<{ label: string; value: string | number }>,
  ) =>
    options.map(({ label, value }) => ({
      label: formatMessage(messages[label]),
      value,
    }))

  return (
    <Wrapper displayDivider={isRegular}>
      <StyledCondition align="center" wrap="wrap">
        <StyledName>{formatMessage(messages.repeat)}</StyledName>
        <SelectInput
          disabled={isFetching}
          label={formatMessage(messages.repeat)}
          options={formatOptions(FORMATTED_REPEAT_ACTIONS)}
          value={formData[REPEAT]}
          onChange={onChangeRecurrence}
          isValidationFailed={Object.prototype.hasOwnProperty.call(
            validationErrors,
            REPEAT,
          )}
        />
      </StyledCondition>
      {isRegular && (
        <>
          <StyledCondition align="center" wrap="wrap" marginBottom={8}>
            <StyledName>{formatMessage(messages.every)}</StyledName>
            <EveryTextField
              required
              disabled={isFetching}
              value={formData[REGULAR_REPEAT_SCHEDULE][FREQUENCY]}
              error={Object.prototype.hasOwnProperty.call(
                validationErrors,
                FREQUENCY,
              )}
              onChange={({ target: { value } }) => {
                onChangeRegularRepeat(+value, FREQUENCY)
              }}
            />
            <SelectInput
              disabled={isFetching}
              options={formatOptions(FORMATTED_STOP_RECURRENCE_ACTIONS)}
              value={formData[REGULAR_REPEAT_SCHEDULE][REPEAT_BY]}
              onChange={(value: string) => {
                onChangeRegularRepeat(value, REPEAT_BY)
              }}
              isValidationFailed={Object.prototype.hasOwnProperty.call(
                validationErrors,
                REPEAT_BY,
              )}
            />
          </StyledCondition>
          {isStartCondition &&
            formData[REGULAR_REPEAT_SCHEDULE][REPEAT_BY] === VALUE_WEEKS && (
              <StyledCondition align="center" wrap="wrap" marginBottom={8}>
                <DaysContainer
                  error={Object.prototype.hasOwnProperty.call(
                    validationErrors,
                    DAYS_OF_WEEK,
                  )}>
                  {DAYS_OF_WEEK_SHORTENED_OPTIONS.map(({ label, value }) => (
                    <Day
                      justify="center"
                      key={label}
                      noBorder={value === 'Sun'}
                      onClick={() => {
                        const oldValue =
                          formData[REGULAR_REPEAT_SCHEDULE][DAYS_OF_WEEK] || []
                        let newValue = []

                        if (oldValue.includes(value)) {
                          newValue = oldValue.filter(oldVal => oldVal !== value)
                        } else {
                          newValue = [...oldValue, value]
                        }

                        onChangeRegularRepeat(newValue, DAYS_OF_WEEK)
                      }}
                      selected={formData[REGULAR_REPEAT_SCHEDULE][
                        DAYS_OF_WEEK
                      ]?.includes(value)}>
                      {formatMessage(messages[label])}
                    </Day>
                  ))}
                </DaysContainer>
              </StyledCondition>
            )}
          {isStartCondition &&
            formData[REGULAR_REPEAT_SCHEDULE][REPEAT_BY] === VALUE_MONTHS && (
              <>
                <StyledCondition align="center" wrap="wrap" marginBottom={8}>
                  <RadioContainer
                    onClick={() => {
                      setMonthRepeatType(0)
                      onClearMonthData()
                    }}>
                    <Radio
                      color="primary"
                      checked={monthRepeatType === 0}
                      onClick={event => {
                        event.preventDefault()
                        setMonthRepeatType(0)
                        onClearMonthData()
                      }}
                    />
                    {formatMessage(messages.each)}
                  </RadioContainer>
                  <SelectInput
                    disabled={isFetching || monthRepeatType === 1}
                    options={DAYS_OF_MONTH}
                    value={formData[REGULAR_REPEAT_SCHEDULE][DAY_OF_MONTH]}
                    onChange={(value: string) => {
                      onChangeRegularRepeat(value, DAY_OF_MONTH)
                    }}
                    isValidationFailed={Object.prototype.hasOwnProperty.call(
                      validationErrors,
                      DAY_OF_MONTH,
                    )}
                  />
                </StyledCondition>
                <StyledCondition align="center" wrap="wrap" marginBottom={15}>
                  <OccurrencesContainer
                    onClick={() => {
                      setMonthRepeatType(1)
                      onClearMonthData()
                    }}>
                    <Radio
                      color="primary"
                      checked={monthRepeatType === 1}
                      onClick={event => {
                        event.preventDefault()
                        setMonthRepeatType(1)
                        onClearMonthData()
                      }}
                    />
                    {formatMessage(messages.onThe)}
                  </OccurrencesContainer>
                  <SelectInput
                    disabled={isFetching || monthRepeatType === 0}
                    options={formatOptions(WEEK_NUMBERS)}
                    value={
                      formData[REGULAR_REPEAT_SCHEDULE][WEEK_ORDINARY_NUMBER]
                    }
                    onChange={(value: string) => {
                      onChangeRegularRepeat(value, WEEK_ORDINARY_NUMBER)
                    }}
                    isValidationFailed={Object.prototype.hasOwnProperty.call(
                      validationErrors,
                      WEEK_ORDINARY_NUMBER,
                    )}
                  />
                  <SelectInput
                    disabled={isFetching || monthRepeatType === 0}
                    options={formatOptions(DAYS_OF_WEEK_OPTIONS)}
                    value={formData[REGULAR_REPEAT_SCHEDULE][DAY_OF_WEEK]}
                    onChange={(value: string) => {
                      onChangeRegularRepeat(value, DAY_OF_WEEK)
                    }}
                    isValidationFailed={Object.prototype.hasOwnProperty.call(
                      validationErrors,
                      DAY_OF_WEEK,
                    )}
                  />
                </StyledCondition>
              </>
            )}
          {isStartCondition && (
            <StyledCondition align="center" wrap="wrap" marginBottom={8}>
              <StyledName>{formatMessage(messages.endsAfter)}</StyledName>
              <OccurrencesTextField
                required
                error={Object.prototype.hasOwnProperty.call(
                  validationErrors,
                  END_DATE_AFTER_NUMBER,
                )}
                disabled={isFetching}
                value={formData[REGULAR_REPEAT_SCHEDULE][END_DATE_AFTER_NUMBER]}
                onChange={({ target: { value } }) => {
                  onChangeRegularRepeat(+value, END_DATE_AFTER_NUMBER)
                }}
              />
              <SelectInput
                disabled={isFetching}
                options={formatOptions(END_DATE_TERMS)}
                value={formData[REGULAR_REPEAT_SCHEDULE][END_DATE_AFTER_TERM]}
                onChange={(value: string) => {
                  onChangeRegularRepeat(value, END_DATE_AFTER_TERM)
                }}
                isValidationFailed={Object.prototype.hasOwnProperty.call(
                  validationErrors,
                  END_DATE_AFTER_TERM,
                )}
              />
            </StyledCondition>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default injectIntl(TriggersSchedule)
